import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/PageUI";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";

export const SecuritySection = () => {
  const { user, refresh } = useAuth();
  const enabled = !!user?.two_factor_enabled;
  const [setup, setSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disarm, setDisarm] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setCode("");
      await refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Two-factor authentication disabled");
      setDisarm(false); setCode("");
      await refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-7 space-y-4 mt-5" data-testid="security-card">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${enabled ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>
          {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-lg">Security &amp; two-factor authentication</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {enabled
              ? "2FA is active. You'll be asked for a 6-digit code from your authenticator app when logging in."
              : "Add an extra layer of protection with an authenticator app (Google Authenticator, Authy, 1Password)."}
          </p>
        </div>
        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 ${enabled ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`} data-testid="twofa-status">
          {enabled ? "Enabled" : "Off"}
        </span>
      </div>

      {!enabled && !setup && (
        <Button onClick={startSetup} disabled={busy} className="brand-gradient text-white border-0" data-testid="twofa-setup-btn">
          {busy ? "Please wait..." : "Set up 2FA"}
        </Button>
      )}

      {!enabled && setup && (
        <div className="rounded-xl border border-border bg-background p-5 space-y-4" data-testid="twofa-setup-panel">
          <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <img src={setup.qr} alt="2FA QR code" className="h-40 w-40 rounded-lg border border-border bg-white p-2" data-testid="twofa-qr" />
            <div className="flex-1 w-full">
              <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
              <code className="block text-xs font-mono break-all bg-muted rounded-lg p-2" data-testid="twofa-secret">{setup.secret}</code>
              <Label className="mt-4 block">Verification code</Label>
              <Input inputMode="numeric" placeholder="6-digit code" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="mt-1.5 h-11 tracking-widest" data-testid="twofa-code-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={enable} disabled={busy || code.length !== 6} className="brand-gradient text-white border-0" data-testid="twofa-enable-btn">
              {busy ? "Verifying..." : "Enable 2FA"}
            </Button>
            <Button variant="outline" onClick={() => { setSetup(null); setCode(""); }} data-testid="twofa-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      {enabled && !disarm && (
        <Button variant="outline" onClick={() => setDisarm(true)} data-testid="twofa-disable-open-btn" className="text-destructive border-destructive/40 hover:bg-destructive/10">
          Disable 2FA
        </Button>
      )}

      {enabled && disarm && (
        <div className="rounded-xl border border-border bg-background p-5 space-y-3" data-testid="twofa-disable-panel">
          <Label>Enter a current 6-digit code to disable 2FA</Label>
          <Input inputMode="numeric" placeholder="6-digit code" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-11 tracking-widest max-w-xs" data-testid="twofa-disable-code-input" />
          <div className="flex gap-2">
            <Button onClick={disable} disabled={busy || code.length !== 6} className="bg-destructive text-white border-0 hover:opacity-90" data-testid="twofa-disable-confirm-btn">
              {busy ? "Please wait..." : "Confirm disable"}
            </Button>
            <Button variant="outline" onClick={() => { setDisarm(false); setCode(""); }} data-testid="twofa-disable-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
